import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMe } from "../api/hooks.ts";
import { NetworkPage, type NetworkView } from "../components/network/network-page.tsx";
import { useT } from "../lib/locale.ts";

/**
 * `/natverk` — the HQ dashboard.
 *
 * The active view is a search parameter rather than a path segment, so the filter state that
 * belongs to the whole screen lives in one component and a link to "the KPI grid" is still a
 * link. Validated on the way in: `?vy=` from a bookmark that predates a renamed tab lands on
 * the overview rather than on an empty page.
 */

const VIEWS: readonly NetworkView[] = [
  "oversikt",
  "kpi",
  "efterfragan",
  "systemhalsa",
  "rapporter",
  "integrationer",
  "kostnader",
  "butik",
  "revisionslogg",
];

export const Route = createFileRoute("/natverk/")({
  validateSearch: (search: Record<string, unknown>): { vy: NetworkView } => {
    const value = search.vy;
    return {
      vy:
        typeof value === "string" && (VIEWS as readonly string[]).includes(value)
          ? (value as NetworkView)
          : "oversikt",
    };
  },
  component: NetworkRoute,
});

function NetworkRoute() {
  const t = useT();
  const me = useMe();
  const { vy } = Route.useSearch();
  const navigate = useNavigate();

  // The server refuses these endpoints anyway; this is what stops the page firing a round of
  // 403s to find that out. `network.countries` is non-empty exactly when the actor may read
  // the dashboard — see the note in `apps/api/src/routes/me.ts`.
  if (me.data !== undefined && me.data.network.countries.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <p className="text-body-sm text-muted-foreground">{t("error.forbidden")}</p>
      </div>
    );
  }

  return (
    <NetworkPage
      view={vy}
      onViewChange={(view) => void navigate({ to: "/natverk", search: { vy: view } })}
    />
  );
}
